import { useState, useEffect, useCallback } from 'react';
import { chatbotService } from '../services';

/**
 * Hook personnalisé pour gérer l'état et les interactions du chatbot
 */
export const useChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [error, setError] = useState(null);

  // Générer un ID utilisateur unique
  const [userId] = useState(() => 
    'user_' + Math.random().toString(36).substr(2, 9)
  );

  // Vérifier la connexion API au montage
  useEffect(() => {
    checkApiConnection();
  }, []);

  // Message de bienvenue au premier chargement
  useEffect(() => {
    if (messages.length === 0) {
      addWelcomeMessage();
    }
  }, [messages.length]);

  const checkApiConnection = useCallback(async () => {
    try {
      const available = await chatbotService.isApiAvailable();
      setIsOnline(available);
      setError(null);
    } catch (err) {
      setIsOnline(false);
      setError('Connexion impossible au service chatbot');
    }
  }, []);

  const addWelcomeMessage = useCallback(() => {
    const welcomeMessage = {
      id: Date.now(),
      text: "👋 Bonjour ! Je suis l'assistant virtuel de S2i. Comment puis-je vous aider aujourd'hui ?",
      isBot: true,
      timestamp: new Date(),
      type: 'welcome'
    };
    setMessages([welcomeMessage]);
  }, []);

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || isLoading) {
      return false;
    }

    // Ajouter le message utilisateur
    const userMessage = {
      id: Date.now(),
      text: messageText.trim(),
      isBot: false,
      timestamp: new Date(),
      type: 'user'
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Envoyer au service chatbot
      const response = await chatbotService.sendMessage(messageText.trim(), userId);
      
      // Ajouter la réponse du bot
      const botMessage = {
        id: Date.now() + 1,
        text: response.response || "Je n'ai pas pu traiter votre demande.",
        isBot: true,
        timestamp: new Date(),
        type: 'response',
        metadata: {
          confidence: response.confidence,
          method: response.method,
          predictedTag: response.predicted_tag,
          responseTime: response.response_time
        }
      };

      setMessages(prev => [...prev, botMessage]);
      return true;

    } catch (err) {
      console.error('Erreur lors de l\'envoi du message:', err);
      
      // Message d'erreur
      const errorMessage = {
        id: Date.now() + 1,
        text: isOnline 
          ? "Désolé, je rencontre des difficultés techniques. Pouvez-vous reformuler votre question ?"
          : "🔌 Service temporairement indisponible. Veuillez réessayer plus tard.",
        isBot: true,
        timestamp: new Date(),
        type: 'error',
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(err.message);
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [userId, isLoading, isOnline]);

  const clearChat = useCallback(() => {
    setMessages([]);
    addWelcomeMessage();
    setError(null);
  }, [addWelcomeMessage]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Récupérer l'historique depuis l'API (optionnel)
  const loadChatHistory = useCallback(async () => {
    try {
      const history = await chatbotService.getChatHistory(20, userId);
      if (history && Array.isArray(history)) {
        const formattedHistory = history.map((item, index) => ({
          id: Date.now() + index,
          text: item.message || item.response,
          isBot: !!item.response,
          timestamp: new Date(item.timestamp),
          type: item.response ? 'response' : 'user'
        }));
        setMessages(formattedHistory);
      }
    } catch (err) {
      console.warn('Impossible de charger l\'historique:', err);
    }
  }, [userId]);

  // Statistiques du chat (optionnel)
  const getChatStats = useCallback(() => {
    const totalMessages = messages.length;
    const userMessages = messages.filter(m => !m.isBot).length;
    const botMessages = messages.filter(m => m.isBot).length;
    
    return {
      totalMessages,
      userMessages,
      botMessages,
      isActive: isOpen,
      hasErrors: messages.some(m => m.isError)
    };
  }, [messages, isOpen]);

  return {
    // État
    isOpen,
    messages,
    isLoading,
    isOnline,
    error,
    userId,
    
    // Actions
    sendMessage,
    clearChat,
    toggleChat,
    closeChat,
    openChat,
    checkApiConnection,
    loadChatHistory,
    
    // Utilitaires
    getChatStats
  };
};

export default useChatbot;
